import { can } from '@selfon/domain/browser';
import { useEffect, useRef, type ReactElement } from 'react';
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { Me } from './api';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Layout, principalOf } from './components/Layout';
import { Loading } from './components/ui';
import { config } from './config';
import { About } from './pages/About';
import { Account } from './pages/Account';
import { Audit } from './pages/Audit';
import { Billing } from './pages/Billing';
import { Clients } from './pages/Clients';
import { CenterNotices, CenterSettings } from './pages/CenterOps';
import { Hq } from './pages/Hq';
import { HqContracts, HqMessaging, HqNotices } from './pages/HqOps';
import { Login } from './pages/Login';
import { CalendarPage } from './pages/calendar/Calendar';
import { Family } from './pages/Family';
import { Student } from './pages/Student';
import { TherapyNotes } from './pages/TherapyNotes';
import { Tasks } from './pages/Tasks';
import { Todos } from './pages/Todos';
import { Feed } from './pages/Feed';
import { Welcome } from './pages/Welcome';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DemoReset } from './pages/DemoReset';
import { Legal } from './pages/Legal';
import { isPublicPath, Pricing, PublicShell, Refund, Support } from './pages/PublicPages';
import { Voucher } from './pages/Voucher';
import { Home } from './pages/Home';
import { CenterCourses, GuardianCourses } from './pages/Courses';
import { Programs } from './pages/Programs';
import { GuardianTeachers, HqTeachers, TeacherDesk } from './pages/Teachers';
import { Accounting } from './pages/Accounting';
import { MyPayslips } from './pages/Payroll';

/** 역할별 첫 화면 */
function homeOf(me: Me) {
  if (me.role === 'HQ_ADMIN' && !me.impersonation) return '/hq';
  if (me.role === 'STUDENT') return '/student';
  if (me.role === 'GUARDIAN') return '/family';
  if (me.role === 'THERAPIST' && !me.centerId) return me.profile?.independent ? '/teacher-desk' : '/welcome';
  return '/home';
}

/** 화면별 브라우저 탭 제목. 없는 주소는 SelfOn 만 보여 준다. */
const PAGE_TITLE: Record<string, string> = {
  '/account': '내 계정', '/about': '서비스 소개', '/pricing': '요금 안내', '/refund': '환불 정책', '/support': '고객센터', '/terms': '이용약관', '/privacy': '개인정보처리방침',
  '/notes': '회기 기록 · SOAP', '/home': '오늘', '/sessions': '일정·출결', '/billing': '수납 현황', '/accounting': '결산 회계 세무', '/clients': '이용자', '/voucher': '바우처 대조',
  '/tasks': '과제', '/todos': '할 일 관리', '/notices': '공지·문의', '/settings': '센터 설정', '/family': '우리 아이', '/courses': '센터 수업', '/center-courses': '센터 수업 관리', '/programs': '프로그램 관리', '/teachers': '선생님 찾기', '/teacher-desk': '선생님 소개', '/my-payslips': '내 임금명세서', '/hq/teachers': '개인 선생님 승인',
  '/student': '오늘의 과제', '/audit': '감사 로그', '/hq': '센터 관리', '/hq/contracts': '계약 심사', '/hq/messaging': '알림톡 승인', '/hq/notices': '공지·문의',
};

function Guard({ ok, children }: { ok: boolean; children: ReactElement }) {
  const { me } = useAuth();
  return ok ? children : <Navigate to={homeOf(me!)} replace />;
}

function AppRoutes() {
  const { me, loading } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // 새로 로그인하면 이전 사용자가 보던 화면이 아니라 그 역할의 첫 화면으로(새로고침은 그대로)
  const prev = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const t = PAGE_TITLE[pathname];
    document.title = t ? `${t} | SelfOn` : 'SelfOn';
  }, [pathname]);
  useEffect(() => {
    if (loading) return;
    const cur = me?.userId ?? null;
    if (prev.current === null && me) navigate(homeOf(me), { replace: true });
    prev.current = cur;
  }, [loading, me, navigate]);
  if (loading) return <div className="center-screen"><Loading what="SelfOn 여는 중" /></div>;
  // 데모 전용 숨은 초기화 화면(안내 파일 참고)
  if (config.authMode === 'demo' && window.location.hash.startsWith('#/demo-reset')) return <DemoReset />;
  // 요금·환불 정책·고객센터·약관은 로그인 없이(결제대행사 심사·센터 문의·로그인이 안 될 때)
  if (!me && isPublicPath(pathname)) {
    return <PublicShell path={pathname}>{pathname === '/about' ? <About /> : pathname === '/pricing' ? <Pricing /> : pathname === '/refund' ? <Refund /> : pathname === '/support' ? <Support /> : <Legal kind={pathname === '/terms' ? 'terms' : 'privacy'} />}</PublicShell>;
  }
  if (!me) return <Login />;
  const p = principalOf(me);
  const inCenter = Boolean(me.centerId);
  // 자녀 모드·학생: 학생 화면만
  if (me.role === 'STUDENT') return (
    <Routes><Route element={<Layout />}><Route path="/student" element={<Student />} />{!me.childMode && <Route path="/todos" element={<Todos />} />}<Route path="*" element={<Navigate to="/student" replace />} /></Route></Routes>
  );
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/terms" element={<Legal kind="terms" />} />
        <Route path="/privacy" element={<Legal kind="privacy" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/support" element={<Support />} />
        <Route path="/about" element={<About />} />
        <Route path="/account" element={<Guard ok={me.role !== 'HQ_ADMIN' && !me.impersonation}><Account /></Guard>} />
        <Route path="/home" element={<Guard ok={inCenter && (me.role === 'CENTER_DIRECTOR' || me.role === 'THERAPIST' || Boolean(me.impersonation))}><Home /></Guard>} />
        <Route path="/welcome" element={<Guard ok={me.role === 'THERAPIST' && !inCenter}><Welcome /></Guard>} />
        <Route path="/family" element={<Guard ok={me.role === 'GUARDIAN'}><Family /></Guard>} />
        <Route path="/feed" element={<Guard ok={(me.role === 'GUARDIAN' && !me.childMode) || (inCenter && (me.role === 'CENTER_DIRECTOR' || me.role === 'THERAPIST')) || (me.role === 'HQ_ADMIN' && !me.impersonation)}><Feed /></Guard>} />
        <Route path="/courses" element={<Guard ok={me.role === 'GUARDIAN' && !me.childMode}><GuardianCourses /></Guard>} />
        <Route path="/my-payslips" element={<Guard ok={inCenter && can(p, 'payroll:read')}><MyPayslips /></Guard>} />
        <Route path="/teachers" element={<Guard ok={me.role === 'GUARDIAN' && !me.childMode}><GuardianTeachers /></Guard>} />
        <Route path="/teacher-desk" element={<Guard ok={(inCenter || Boolean(me.profile?.independent)) && can(p, 'teacher:profile')}><TeacherDesk /></Guard>} />
        <Route path="/center-courses" element={<Guard ok={inCenter && can(p, 'course:manage')}><CenterCourses /></Guard>} />
        <Route path="/programs" element={<Guard ok={inCenter && can(p, 'course:manage')}><Programs /></Guard>} />
        <Route path="/accounting" element={<Guard ok={inCenter && can(p, 'closing:manage')}><Accounting /></Guard>} />
        <Route path="/billing" element={<Guard ok={inCenter && can(p, 'ledger:read')}><Billing /></Guard>} />
        <Route path="/clients" element={<Guard ok={inCenter && can(p, 'client:read')}><Clients /></Guard>} />
        <Route path="/sessions" element={<Guard ok={inCenter && can(p, 'session:read')}><CalendarPage /></Guard>} />
        <Route path="/voucher" element={<Guard ok={inCenter && can(p, 'voucher:audit')}><Voucher /></Guard>} />
        <Route path="/notes" element={<Guard ok={inCenter && !me.impersonation && (me.role==='CENTER_DIRECTOR'||me.role==='THERAPIST')}><TherapyNotes /></Guard>} />
        <Route path="/tasks" element={<Guard ok={inCenter && can(p, 'task:read')}><Tasks /></Guard>} />
        <Route path="/todos" element={<Guard ok={can(p, 'todo:use')}><Todos /></Guard>} />
        <Route path="/audit" element={<Guard ok={can(p, 'audit:read')}><Audit /></Guard>} />
        <Route path="/notices" element={<Guard ok={inCenter && can(p, 'notice:read')}><CenterNotices /></Guard>} />
        <Route path="/settings" element={<Guard ok={inCenter && (can(p, 'contract:request') || Boolean(me.impersonation))}><CenterSettings /></Guard>} />
        <Route path="/hq" element={<Guard ok={me.role === 'HQ_ADMIN' && !me.impersonation}><Hq /></Guard>} />
        <Route path="/hq/contracts" element={<Guard ok={can(p, 'contract:decide') && !me.impersonation}><HqContracts /></Guard>} />
        <Route path="/hq/teachers" element={<Guard ok={can(p, 'teacher:review') && !me.impersonation}><HqTeachers /></Guard>} />
        <Route path="/hq/messaging" element={<Guard ok={can(p, 'messaging:decide') && !me.impersonation}><HqMessaging /></Guard>} />
        <Route path="/hq/notices" element={<Guard ok={can(p, 'notice:publish') && !me.impersonation}><HqNotices /></Guard>} />
        <Route path="*" element={<Navigate to={homeOf(me)} replace />} />
      </Route>
    </Routes>
  );
}

// 단일 HTML 데모는 file:// 로 열리므로 주소 경로 대신 # 경로를 쓴다
const Router = config.authMode === 'demo' ? HashRouter : BrowserRouter;

export function App() {
  return (
    <AuthProvider>
      <Router>
        {/* # 경로 라우터와 충돌하지 않도록 주소를 바꾸지 않고 본문으로 포커스만 옮긴다 */}
        <a className="skip-link" href="#main" onClick={(e) => { e.preventDefault(); document.getElementById('main')?.focus(); }}>본문으로 건너뛰기</a>
        <ErrorBoundary><AppRoutes /></ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}
